/**
 * Marketing Settings — Bloq-admin-only page for editing the public
 * marketing surface without touching code.
 *
 * Covers:
 *   - Cognito form embed codes for /fund-a-feature and /invest pages.
 *   - Pricing tiers (name/tagline/price/storage/features/highlight/CTA).
 *
 * Roadmap items are managed from the existing /app/roadmap kanban — there
 * you toggle `is_public=true` on a card and set `target_quarter`, and it
 * flows out to the public /roadmap page automatically.
 */
import { useEffect, useState } from "react";
import { toast } from "sonner";
import { Plus, Trash2, Star, Eye, EyeOff, ArrowUp, ArrowDown, Check, X } from "lucide-react";
import api, { formatApiError } from "@/lib/api";
import HtmlEmbed from "@/components/HtmlEmbed";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from "@/components/ui/dialog";

export default function MarketingSettings() {
  const [tab, setTab] = useState("embeds");
  return (
    <div className="bloq-container py-6 max-w-5xl">
      <div className="mb-6">
        <p className="bloq-eyebrow">Bloq admin</p>
        <h1 className="bloq-headline text-3xl mt-1">Marketing settings</h1>
        <p className="text-sm text-[#82828c] mt-2 max-w-2xl">
          Edit what shows on the public marketing site — Cognito form embeds,
          pricing tier features, storage limits, and CTAs. Changes go live the
          moment you save.
        </p>
      </div>

      <div className="flex items-center gap-1 bg-[#f5efe6] p-1 rounded-md self-start mb-6 inline-flex">
        <TabBtn active={tab === "embeds"} onClick={() => setTab("embeds")} testid="mkt-tab-embeds">Form embeds</TabBtn>
        <TabBtn active={tab === "pricing"} onClick={() => setTab("pricing")} testid="mkt-tab-pricing">Pricing tiers</TabBtn>
        <TabBtn active={tab === "matrix"} onClick={() => setTab("matrix")} testid="mkt-tab-matrix">Comparison matrix</TabBtn>
      </div>

      {tab === "embeds" ? <EmbedsPanel /> : tab === "pricing" ? <PricingPanel /> : <MatrixPanel />}
    </div>
  );
}

function TabBtn({ active, onClick, children, testid }) {
  return (
    <button
      type="button"
      onClick={onClick}
      data-testid={testid}
      className={`px-3 py-1 text-xs uppercase tracking-wider font-semibold rounded transition-colors
        ${active ? "bg-white text-[#005e7a] shadow-sm" : "text-[#82828c] hover:text-[#1a1a1f]"}`}
    >
      {children}
    </button>
  );
}

// ----------------------------------------------------------------------------
// Embeds — single doc with fund_a_feature_embed + invest_embed.
// ----------------------------------------------------------------------------
function EmbedsPanel() {
  const [fund, setFund] = useState("");
  const [invest, setInvest] = useState("");
  const [beta, setBeta] = useState("");
  const [feedback, setFeedback] = useState("");
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    api.get("/marketing/settings").then((r) => {
      setFund(r.data?.fund_a_feature_embed || "");
      setInvest(r.data?.invest_embed || "");
      setBeta(r.data?.beta_signup_embed || "");
      setFeedback(r.data?.feedback_embed || "");
    }).finally(() => setLoading(false));
  }, []);

  const save = async () => {
    setSaving(true);
    try {
      await api.patch("/marketing/settings", {
        fund_a_feature_embed: fund,
        invest_embed: invest,
        beta_signup_embed: beta,
        feedback_embed: feedback,
      });
      toast.success("Saved — both pages will reflect the new embed on next load.");
    } catch (e) { toast.error(formatApiError(e)); }
    finally { setSaving(false); }
  };

  if (loading) return <p className="text-[#82828c]">Loading…</p>;
  return (
    <div className="space-y-6">
      <EmbedBox
        title="Fund a Feature — Cognito form embed"
        help="Paste the full <script> + <div> code from Cognito Forms. Appears in the form card on /fund-a-feature."
        value={fund}
        onChange={setFund}
        testid="embed-fund"
      />
      <EmbedBox
        title="Invest in Bloq — Cognito form embed"
        help="Paste the Cognito Forms HTML for the investor-interest form. Appears on /invest below the TAM/ICP/Wedge cards."
        value={invest}
        onChange={setInvest}
        testid="embed-invest"
      />
      <EmbedBox
        title="Beta Signup — Cognito form embed"
        help="Paste the Cognito Forms HTML for the beta tester application form. Appears on /beta-signup below the perks grid."
        value={beta}
        onChange={setBeta}
        testid="embed-beta"
      />
      <EmbedBox
        title="Feedback & bug reports — Cognito form embed"
        help="Paste the Cognito Forms HTML for the feedback / bug report form. Appears on /feedback."
        value={feedback}
        onChange={setFeedback}
        testid="embed-feedback"
      />
      <button
        onClick={save}
        disabled={saving}
        className="bloq-btn-primary"
        data-testid="embed-save"
      >
        {saving ? "Saving…" : "Save embeds"}
      </button>
    </div>
  );
}

function EmbedBox({ title, help, value, onChange, testid }) {
  // Preview state — admins can sanity-check the live render BEFORE saving.
  // Catches the common "wrong key / typo / wrong form id" mistakes early.
  const [showPreview, setShowPreview] = useState(false);
  return (
    <div className="bloq-card p-5">
      <div className="flex items-center justify-between gap-3 flex-wrap">
        <div>
          <p className="bloq-headline text-base text-[#005e7a]">{title}</p>
          <p className="text-xs text-[#82828c] mt-1">{help}</p>
        </div>
        <button
          type="button"
          onClick={() => setShowPreview((v) => !v)}
          className="bloq-btn-secondary text-xs inline-flex items-center gap-1.5 flex-shrink-0"
          data-testid={`${testid}-preview-toggle`}
        >
          {showPreview ? <EyeOff className="w-3.5 h-3.5" /> : <Eye className="w-3.5 h-3.5" />}
          {showPreview ? "Hide preview" : "Preview live"}
        </button>
      </div>
      <textarea
        rows={6}
        className="bloq-input font-mono text-xs mt-3"
        placeholder="<!-- paste Cognito embed code here -->"
        value={value}
        onChange={(e) => onChange(e.target.value)}
        data-testid={testid}
      />
      {showPreview && (
        <div className="mt-4 border-t border-[#e5e0d6] pt-4">
          <p className="text-[10px] uppercase tracking-widest text-[#82828c] mb-2">Live preview</p>
          {value ? (
            <HtmlEmbed
              key={value}
              html={value}
              className="bg-[#fbfcfc] p-4 border border-[#e5e0d6]"
              testId={`${testid}-preview`}
            />
          ) : (
            <p className="text-xs text-[#82828c] italic p-4 border border-dashed border-[#e5e0d6]">
              Nothing pasted yet.
            </p>
          )}
        </div>
      )}
    </div>
  );
}

// ----------------------------------------------------------------------------
// Pricing — list + per-tier editor in a dialog.
// ----------------------------------------------------------------------------
function PricingPanel() {
  const [rows, setRows] = useState([]);
  const [edit, setEdit] = useState(null);
  const load = () => api.get("/marketing/admin/pricing").then((r) => setRows(r.data));
  useEffect(() => { load(); }, []);
  return (
    <div className="space-y-4">
      <p className="text-sm text-[#4a4a52]">
        Edit each tier's name, price, storage limit, CTA, and feature list.
        Features support a "Coming soon" badge.
      </p>
      <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-4" data-testid="pricing-grid">
        {rows.map((t) => (
          <div
            key={t.tier_id}
            className={`bloq-card p-5 flex flex-col gap-2 ${t.highlight ? "ring-2 ring-[#005e7a]" : ""}`}
            data-testid={`pricing-card-${t.tier_id}`}
          >
            <div className="flex items-start justify-between">
              <p className="bloq-headline text-xl text-[#005e7a]">{t.name}</p>
              {t.highlight && <Star className="w-4 h-4 text-[#b45309] fill-[#b45309]" />}
            </div>
            <p className="text-xs text-[#82828c]">{t.tagline}</p>
            <p className="text-2xl font-bold mt-1">
              {t.price_monthly === 0
                ? "Free"
                : t.price_monthly == null
                ? "Custom"
                : <>${t.price_monthly}<span className="text-sm text-[#82828c] font-normal">/mo</span></>}
            </p>
            <p className="text-xs text-[#82828c]">Storage: {t.storage_gb} GB</p>
            <p className="text-xs text-[#82828c]">{t.features?.length || 0} features</p>
            <button
              className="bloq-btn-secondary text-xs px-3 py-1.5 mt-3 self-start"
              onClick={() => setEdit(t)}
              data-testid={`edit-tier-${t.tier_id}`}
            >Edit</button>
          </div>
        ))}
      </div>
      <TierDialog tier={edit} open={!!edit} onClose={() => setEdit(null)} onSaved={() => { setEdit(null); load(); }} />
    </div>
  );
}

function TierDialog({ tier, open, onClose, onSaved }) {
  const [form, setForm] = useState(null);
  useEffect(() => { setForm(tier ? { ...tier, features: [...(tier.features || [])] } : null); }, [tier]);
  if (!form) return null;

  const addFeature = () => setForm({ ...form, features: [...(form.features || []), { label: "", included: true, badge: "" }] });
  const setFeature = (i, patch) => setForm({ ...form, features: form.features.map((f, j) => j === i ? { ...f, ...patch } : f) });
  const delFeature = (i) => setForm({ ...form, features: form.features.filter((_, j) => j !== i) });

  const save = async () => {
    try {
      await api.patch(`/marketing/pricing/${form.tier_id}`, form);
      toast.success("Saved");
      onSaved();
    } catch (e) { toast.error(formatApiError(e)); }
  };

  return (
    <Dialog open={open} onOpenChange={(v) => !v && onClose()}>
      <DialogContent className="max-w-3xl max-h-[88vh] overflow-y-auto" data-testid="tier-dialog">
        <DialogHeader><DialogTitle>Edit {form.name} tier</DialogTitle></DialogHeader>
        <div className="grid grid-cols-2 gap-3">
          <FieldBox label="Name">
            <input className="bloq-input" value={form.name} onChange={(e) => setForm({ ...form, name: e.target.value })} data-testid="tier-name" />
          </FieldBox>
          <FieldBox label="Storage (GB)">
            <input type="number" className="bloq-input" value={form.storage_gb ?? ""} onChange={(e) => setForm({ ...form, storage_gb: Number(e.target.value) })} data-testid="tier-storage" />
          </FieldBox>
          <FieldBox label="Tagline" full>
            <input className="bloq-input" value={form.tagline || ""} onChange={(e) => setForm({ ...form, tagline: e.target.value })} data-testid="tier-tagline" />
          </FieldBox>
          <FieldBox label="Price / month (USD)">
            <input type="number" className="bloq-input" placeholder="Leave blank for Custom" value={form.price_monthly ?? ""} onChange={(e) => setForm({ ...form, price_monthly: e.target.value === "" ? null : Number(e.target.value) })} data-testid="tier-price-monthly" />
          </FieldBox>
          <FieldBox label="Price / year (USD)">
            <input type="number" className="bloq-input" placeholder="Leave blank for Custom" value={form.price_yearly ?? ""} onChange={(e) => setForm({ ...form, price_yearly: e.target.value === "" ? null : Number(e.target.value) })} data-testid="tier-price-yearly" />
          </FieldBox>
          <FieldBox label="Annual: shown /mo (optional)">
            <input
              type="number"
              step="0.01"
              className="bloq-input"
              placeholder="auto (price_yearly ÷ 12)"
              value={form.annual_monthly_price ?? ""}
              onChange={(e) => setForm({ ...form, annual_monthly_price: e.target.value === "" ? null : Number(e.target.value) })}
              data-testid="tier-annual-monthly-price"
            />
            <p className="text-[11px] text-[#82828c] mt-1">
              Per-month price shown under the &ldquo;Annual&rdquo; toggle. Blank = computed as <code>price_yearly ÷ 12</code>.
            </p>
          </FieldBox>
          <FieldBox label="CTA label">
            <input className="bloq-input" value={form.cta_label || ""} onChange={(e) => setForm({ ...form, cta_label: e.target.value })} data-testid="tier-cta-label" />
          </FieldBox>
          <FieldBox label="CTA URL">
            <input className="bloq-input" value={form.cta_url || ""} onChange={(e) => setForm({ ...form, cta_url: e.target.value })} data-testid="tier-cta-url" />
          </FieldBox>
          <FieldBox label="Highlight as recommended">
            <label className="flex items-center gap-2 text-sm">
              <input type="checkbox" checked={!!form.highlight} onChange={(e) => setForm({ ...form, highlight: e.target.checked })} data-testid="tier-highlight" />
              Show as the recommended plan
            </label>
          </FieldBox>
          <FieldBox label="Active">
            <label className="flex items-center gap-2 text-sm">
              <input type="checkbox" checked={form.active !== false} onChange={(e) => setForm({ ...form, active: e.target.checked })} data-testid="tier-active" />
              Show on the public pricing page
            </label>
          </FieldBox>
        </div>

        <div className="mt-6">
          <div className="flex items-center justify-between mb-2">
            <p className="bloq-headline text-base">Features</p>
            <button className="bloq-btn-secondary text-xs px-3 py-1.5 inline-flex items-center gap-2" onClick={addFeature} data-testid="tier-add-feature">
              <Plus className="w-3 h-3" /> Add feature
            </button>
          </div>
          <p className="text-xs text-[#82828c] mb-3">Each row: feature label, included/excluded, and optional "Coming soon" badge.</p>
          <div className="flex flex-col gap-2">
            {(form.features || []).map((f, i) => (
              <div key={i} className="bg-[#f5efe6] p-2.5 rounded grid grid-cols-12 gap-2 items-center" data-testid={`tier-feature-${i}`}>
                <input
                  className="bloq-input col-span-6"
                  placeholder="e.g. Custom booking links"
                  value={f.label}
                  onChange={(e) => setFeature(i, { label: e.target.value })}
                  data-testid={`tier-feature-label-${i}`}
                />
                <label className="col-span-3 text-xs flex items-center gap-2">
                  <input type="checkbox" checked={!!f.included} onChange={(e) => setFeature(i, { included: e.target.checked })} data-testid={`tier-feature-included-${i}`} />
                  Included
                </label>
                <select
                  className="bloq-input col-span-2 text-xs py-1"
                  value={f.badge || ""}
                  onChange={(e) => setFeature(i, { badge: e.target.value })}
                  data-testid={`tier-feature-badge-${i}`}
                >
                  <option value="">No badge</option>
                  <option value="coming_soon">Coming soon</option>
                  <option value="new">New</option>
                  <option value="beta">Beta</option>
                </select>
                <button className="col-span-1 text-[#c43838]" onClick={() => delFeature(i)} data-testid={`tier-feature-delete-${i}`}>
                  <Trash2 className="w-4 h-4 mx-auto" />
                </button>
              </div>
            ))}
          </div>
        </div>

        <DialogFooter className="mt-6">
          <button onClick={save} className="bloq-btn-primary" data-testid="tier-save">Save tier</button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

function FieldBox({ label, children, full }) {
  return (
    <div className={full ? "col-span-2" : ""}>
      <label className="text-xs uppercase tracking-widest text-[#82828c] font-medium block mb-1.5">{label}</label>
      {children}
    </div>
  );
}


// ----------------------------------------------------------------------------
// Comparison matrix — backend-powered editor for the public /pricing page's
// Full Feature Comparison section. Two collections behind the scenes:
//   pricing_matrix_plans  — 5 plan columns (free/lite/premium/pro/business)
//   pricing_matrix_rows   — N feature rows, each with values keyed by slug
//
// Slug is NOT editable post-seed because rows reference plans by slug;
// editing slug would orphan every cell.
// ----------------------------------------------------------------------------
function MatrixPanel() {
  const [plans, setPlans] = useState([]);
  const [rows, setRows] = useState([]);
  const [loading, setLoading] = useState(true);

  const load = async () => {
    setLoading(true);
    try {
      const r = await api.get("/marketing/admin/pricing-matrix");
      setPlans(r.data?.plans || []);
      setRows(r.data?.rows || []);
    } catch (e) {
      toast.error(formatApiError(e));
    } finally {
      setLoading(false);
    }
  };
  useEffect(() => { load(); }, []);

  if (loading) {
    return <p className="text-sm text-[#82828c]" data-testid="matrix-loading">Loading matrix…</p>;
  }
  return (
    <div className="space-y-8">
      <MatrixPlansSection plans={plans} onReload={load} />
      <MatrixRowsSection plans={plans} rows={rows} onReload={load} />
    </div>
  );
}

// --- Plans (5 columns) -----------------------------------------------------
function MatrixPlansSection({ plans, onReload }) {
  return (
    <div>
      <h2 className="bloq-headline text-xl mb-1">Matrix plans</h2>
      <p className="text-xs text-[#82828c] mb-4">
        These columns drive the Full Feature Comparison section on the public pricing page.
        The plan <code>slug</code> isn&rsquo;t editable here because feature rows reference plans by slug.
      </p>
      <div className="bloq-card p-3 sm:p-4 overflow-x-auto">
        <table className="w-full text-xs sm:text-sm">
          <thead>
            <tr className="text-[#82828c] text-[10px] uppercase tracking-widest">
              <th className="text-left py-2 px-2">Name</th>
              <th className="text-left py-2 px-2">Slug</th>
              <th className="text-left py-2 px-2">$ / mo</th>
              <th className="text-left py-2 px-2">$ / mo annual</th>
              <th className="text-left py-2 px-2">Order</th>
              <th className="text-left py-2 px-2">Active</th>
              <th className="text-left py-2 px-2"></th>
            </tr>
          </thead>
          <tbody>
            {plans.map((p) => (
              <MatrixPlanEditor key={p.plan_id} plan={p} onSaved={onReload} />
            ))}
          </tbody>
        </table>
      </div>
    </div>
  );
}

function MatrixPlanEditor({ plan, onSaved }) {
  const [form, setForm] = useState(plan);
  const [saving, setSaving] = useState(false);
  useEffect(() => setForm(plan), [plan]);

  const dirty =
    form.name !== plan.name ||
    Number(form.monthly_price) !== Number(plan.monthly_price) ||
    Number(form.annual_equivalent_price) !== Number(plan.annual_equivalent_price) ||
    Number(form.display_order) !== Number(plan.display_order) ||
    Boolean(form.is_active) !== Boolean(plan.is_active);

  const save = async () => {
    setSaving(true);
    try {
      await api.patch(`/marketing/admin/pricing-matrix/plans/${plan.plan_id}`, {
        name: form.name,
        monthly_price: Number(form.monthly_price) || 0,
        annual_equivalent_price: Number(form.annual_equivalent_price) || 0,
        display_order: Number(form.display_order) || 0,
        is_active: !!form.is_active,
      });
      toast.success("Plan saved");
      onSaved();
    } catch (e) {
      toast.error(formatApiError(e));
    } finally {
      setSaving(false);
    }
  };

  return (
    <tr className="border-t border-[#f0eadc]" data-testid={`matrix-plan-${plan.slug}`}>
      <td className="py-2 px-2">
        <input
          className="bloq-input"
          value={form.name || ""}
          onChange={(e) => setForm({ ...form, name: e.target.value })}
          data-testid={`matrix-plan-${plan.slug}-name`}
        />
      </td>
      <td className="py-2 px-2 text-[#82828c] font-mono text-xs">{plan.slug}</td>
      <td className="py-2 px-2">
        <input
          type="number"
          className="bloq-input w-24"
          value={form.monthly_price ?? 0}
          onChange={(e) => setForm({ ...form, monthly_price: e.target.value })}
          data-testid={`matrix-plan-${plan.slug}-monthly`}
        />
      </td>
      <td className="py-2 px-2">
        <input
          type="number"
          className="bloq-input w-24"
          value={form.annual_equivalent_price ?? 0}
          onChange={(e) => setForm({ ...form, annual_equivalent_price: e.target.value })}
          data-testid={`matrix-plan-${plan.slug}-annual`}
        />
      </td>
      <td className="py-2 px-2">
        <input
          type="number"
          className="bloq-input w-16"
          value={form.display_order ?? 0}
          onChange={(e) => setForm({ ...form, display_order: e.target.value })}
          data-testid={`matrix-plan-${plan.slug}-order`}
        />
      </td>
      <td className="py-2 px-2">
        <input
          type="checkbox"
          checked={!!form.is_active}
          onChange={(e) => setForm({ ...form, is_active: e.target.checked })}
          data-testid={`matrix-plan-${plan.slug}-active`}
        />
      </td>
      <td className="py-2 px-2">
        <button
          className="bloq-btn-secondary text-xs px-3 py-1.5 disabled:opacity-50"
          disabled={!dirty || saving}
          onClick={save}
          data-testid={`matrix-plan-${plan.slug}-save`}
        >
          {saving ? "Saving…" : "Save"}
        </button>
      </td>
    </tr>
  );
}

// --- Rows (feature comparison cells) ---------------------------------------
function MatrixRowsSection({ plans, rows, onReload }) {
  const [creating, setCreating] = useState(false);

  const createRow = async () => {
    setCreating(true);
    try {
      await api.post("/marketing/admin/pricing-matrix/rows", {
        feature_name: "New feature",
        category: "",
      });
      toast.success("Row added");
      onReload();
    } catch (e) {
      toast.error(formatApiError(e));
    } finally {
      setCreating(false);
    }
  };

  return (
    <div>
      <div className="flex items-end justify-between flex-wrap gap-3 mb-3">
        <div>
          <h2 className="bloq-headline text-xl">Feature rows</h2>
          <p className="text-xs text-[#82828c] mt-1">
            Each row corresponds to a line in the public comparison table. Choose
            <strong> Included</strong>, <strong>Excluded</strong>, or <strong>Text</strong> for each plan&rsquo;s cell.
          </p>
        </div>
        <button
          className="bloq-btn-secondary text-xs px-3 py-1.5 inline-flex items-center gap-2"
          onClick={createRow}
          disabled={creating}
          data-testid="matrix-add-row"
        >
          <Plus className="w-3.5 h-3.5" /> {creating ? "Adding…" : "Add row"}
        </button>
      </div>
      <div className="space-y-3">
        {rows.map((row, i) => (
          <MatrixRowEditor
            key={row.row_id}
            row={row}
            plans={plans}
            siblingAbove={rows[i - 1]}
            siblingBelow={rows[i + 1]}
            onReload={onReload}
          />
        ))}
      </div>
      {rows.length === 0 && (
        <p className="text-sm text-[#82828c] py-6 text-center" data-testid="matrix-rows-empty">
          No rows yet. Click <strong>Add row</strong> to create one.
        </p>
      )}
    </div>
  );
}

function MatrixRowEditor({ row, plans, siblingAbove, siblingBelow, onReload }) {
  const [form, setForm] = useState(row);
  const [saving, setSaving] = useState(false);
  useEffect(() => setForm(row), [row]);

  const setValueFor = (slug, patch) => {
    const next = { ...(form.values || {}) };
    next[slug] = { ...(next[slug] || { type: "text", value: "" }), ...patch };
    setForm({ ...form, values: next });
  };

  const dirty = JSON.stringify(form) !== JSON.stringify(row);

  const save = async () => {
    setSaving(true);
    try {
      await api.patch(`/marketing/admin/pricing-matrix/rows/${row.row_id}`, {
        feature_name: form.feature_name,
        category: form.category,
        display_order: Number(form.display_order) || 0,
        is_active: !!form.is_active,
        values: form.values || {},
      });
      toast.success("Row saved");
      onReload();
    } catch (e) {
      toast.error(formatApiError(e));
    } finally {
      setSaving(false);
    }
  };

  const remove = async () => {
    if (!window.confirm(`Delete feature "${row.feature_name}"?`)) return;
    try {
      await api.delete(`/marketing/admin/pricing-matrix/rows/${row.row_id}`);
      toast.success("Row deleted");
      onReload();
    } catch (e) {
      toast.error(formatApiError(e));
    }
  };

  const swapOrder = async (otherRow) => {
    if (!otherRow) return;
    try {
      await Promise.all([
        api.patch(`/marketing/admin/pricing-matrix/rows/${row.row_id}`, { display_order: otherRow.display_order }),
        api.patch(`/marketing/admin/pricing-matrix/rows/${otherRow.row_id}`, { display_order: row.display_order }),
      ]);
      onReload();
    } catch (e) {
      toast.error(formatApiError(e));
    }
  };

  return (
    <div
      className="bloq-card p-4"
      data-testid={`matrix-row-${row.row_id}`}
    >
      <div className="flex items-start gap-3 flex-wrap">
        <div className="flex-1 min-w-[200px]">
          <label className="text-[10px] uppercase tracking-widest text-[#82828c] font-medium block mb-1">Feature name</label>
          <input
            className="bloq-input"
            value={form.feature_name || ""}
            onChange={(e) => setForm({ ...form, feature_name: e.target.value })}
            data-testid={`matrix-row-${row.row_id}-name`}
          />
        </div>
        <div className="w-40">
          <label className="text-[10px] uppercase tracking-widest text-[#82828c] font-medium block mb-1">Category</label>
          <input
            className="bloq-input"
            value={form.category || ""}
            onChange={(e) => setForm({ ...form, category: e.target.value })}
            data-testid={`matrix-row-${row.row_id}-category`}
          />
        </div>
        <div className="w-20">
          <label className="text-[10px] uppercase tracking-widest text-[#82828c] font-medium block mb-1">Order</label>
          <input
            type="number"
            className="bloq-input"
            value={form.display_order ?? 0}
            onChange={(e) => setForm({ ...form, display_order: e.target.value })}
            data-testid={`matrix-row-${row.row_id}-order`}
          />
        </div>
        <div>
          <label className="text-[10px] uppercase tracking-widest text-[#82828c] font-medium block mb-1">Active</label>
          <div className="h-9 flex items-center">
            <input
              type="checkbox"
              checked={!!form.is_active}
              onChange={(e) => setForm({ ...form, is_active: e.target.checked })}
              data-testid={`matrix-row-${row.row_id}-active`}
            />
          </div>
        </div>
        <div>
          <label className="text-[10px] uppercase tracking-widest text-[#82828c] font-medium block mb-1">Reorder</label>
          <div className="flex items-center gap-1">
            <button
              type="button"
              className="bloq-btn-secondary text-xs p-1.5 disabled:opacity-40"
              disabled={!siblingAbove}
              onClick={() => swapOrder(siblingAbove)}
              title="Move up"
              data-testid={`matrix-row-${row.row_id}-up`}
            >
              <ArrowUp className="w-3.5 h-3.5" />
            </button>
            <button
              type="button"
              className="bloq-btn-secondary text-xs p-1.5 disabled:opacity-40"
              disabled={!siblingBelow}
              onClick={() => swapOrder(siblingBelow)}
              title="Move down"
              data-testid={`matrix-row-${row.row_id}-down`}
            >
              <ArrowDown className="w-3.5 h-3.5" />
            </button>
          </div>
        </div>
      </div>

      <div className="mt-4 overflow-x-auto">
        <div className="grid gap-2" style={{ gridTemplateColumns: `repeat(${plans.length}, minmax(160px, 1fr))` }}>
          {plans.map((p) => (
            <MatrixCellEditor
              key={p.plan_id}
              plan={p}
              cell={form.values?.[p.slug] || { type: "text", value: "" }}
              onChange={(patch) => setValueFor(p.slug, patch)}
              testidPrefix={`matrix-row-${row.row_id}-${p.slug}`}
            />
          ))}
        </div>
      </div>

      <div className="mt-4 flex items-center justify-end gap-2">
        <button
          className="text-xs text-[#c43838] inline-flex items-center gap-1.5 px-3 py-1.5 hover:underline"
          onClick={remove}
          data-testid={`matrix-row-${row.row_id}-delete`}
        >
          <Trash2 className="w-3.5 h-3.5" /> Delete row
        </button>
        <button
          className="bloq-btn-primary text-xs px-3 py-1.5 disabled:opacity-50"
          disabled={!dirty || saving}
          onClick={save}
          data-testid={`matrix-row-${row.row_id}-save`}
        >
          {saving ? "Saving…" : "Save row"}
        </button>
      </div>
    </div>
  );
}

function MatrixCellEditor({ plan, cell, onChange, testidPrefix }) {
  return (
    <div className="bg-[#fbfcfc] border border-[#f0eadc] rounded-md p-2.5">
      <p className="text-[10px] uppercase tracking-widest text-[#82828c] font-semibold mb-2">{plan.name}</p>
      <select
        className="bloq-input text-xs py-1 mb-2"
        value={cell.type || "text"}
        onChange={(e) => {
          const t = e.target.value;
          if (t === "included") onChange({ type: "included", value: true });
          else if (t === "excluded") onChange({ type: "excluded", value: false });
          else onChange({ type: "text", value: typeof cell.value === "string" ? cell.value : "" });
        }}
        data-testid={`${testidPrefix}-type`}
      >
        <option value="included">Included (✓)</option>
        <option value="excluded">Excluded (✗)</option>
        <option value="text">Text</option>
      </select>
      {cell.type === "text" ? (
        <input
          className="bloq-input text-xs"
          placeholder="e.g. Up to 50"
          value={typeof cell.value === "string" ? cell.value : ""}
          onChange={(e) => onChange({ type: "text", value: e.target.value })}
          data-testid={`${testidPrefix}-value`}
        />
      ) : cell.type === "included" ? (
        <div className="flex items-center justify-center h-7 text-[#15803d]" data-testid={`${testidPrefix}-preview`}>
          <Check className="w-4 h-4" strokeWidth={2.5} />
        </div>
      ) : (
        <div className="flex items-center justify-center h-7 text-[#c4c4cb]" data-testid={`${testidPrefix}-preview`}>
          <X className="w-4 h-4" />
        </div>
      )}
    </div>
  );
}
